"use strict";
/*
 * Copyright 2025, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const react_dom_1 = require("react-dom");
const react_i18next_1 = require("react-i18next");
const maplibre_1 = require("react-map-gl/maplibre");
const projectBaseMapLayers_1 = require("../../config/projectBaseMapLayers");
/**
 * MapLibre IControl that owns the gear menu node.
 * The map inserts this element; do not attach it to the DOM yourself.
 * @see https://maplibre.org/maplibre-gl-js/docs/API/interfaces/IControl/
 */
class MapControlsControl {
    onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group tr__map-controls';
        return this._container;
    }
    onRemove() {
        this._container.remove();
        this._map = undefined;
    }
    getDefaultPosition() {
        return 'top-right';
    }
    getContainer() {
        return this._container;
    }
}
/** Reusable layer option item for the dropdown */
const LayerOption = ({ layerType, label, isActive, disabled = false, suffix, onSelect }) => (react_1.default.createElement("p", { onClick: (e) => {
        e.stopPropagation();
        if (!disabled) {
            onSelect(layerType);
        }
    }, className: `tr__map-controls-dropdown-item ${isActive ? '_active' : ''}`, style: disabled
        ? {
            opacity: 0.5,
            cursor: 'not-allowed',
            pointerEvents: 'none'
        }
        : undefined, role: "button", tabIndex: disabled ? -1 : 0, onKeyDown: (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            e.stopPropagation();
            onSelect(layerType);
        }
    } },
    label,
    suffix));
const MapControlsMenu = ({ currentLayer, currentZoom, overlayOpacity, overlayColor, onLayerChange, onOverlayOpacityChange, onOverlayColorChange, onResetView }) => {
    const { t } = (0, react_i18next_1.useTranslation)(['main', 'transit']);
    const [isOpen, setIsOpen] = react_1.default.useState(false);
    const [localOpacity, setLocalOpacity] = react_1.default.useState(overlayOpacity);
    const [localColor, setLocalColor] = react_1.default.useState(overlayColor);
    react_1.default.useEffect(() => {
        setLocalOpacity(overlayOpacity);
    }, [overlayOpacity]);
    react_1.default.useEffect(() => {
        setLocalColor(overlayColor);
    }, [overlayColor]);
    react_1.default.useEffect(() => {
        const handleClickOutside = (e) => {
            if (!(e.target instanceof Node) || !e.target.closest('.tr__map-controls')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);
    const handleSelect = (layerType) => {
        onLayerChange(layerType);
        setIsOpen(false);
    };
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("button", { className: "maplibregl-ctrl-icon tr__map-controls-button", type: "button", title: t('main:map.controls.title'), "aria-label": t('main:map.controls.title'), onClick: () => setIsOpen((prev) => !prev) },
            react_1.default.createElement("i", { className: "fa fa-cog", "aria-hidden": "true" })),
        react_1.default.createElement("div", { className: "tr__map-controls-dropdown", style: { display: isOpen ? 'flex' : 'none' } },
            react_1.default.createElement("p", { className: "tr__map-controls-dropdown-item", onClick: (e) => {
                    e.stopPropagation();
                    onResetView();
                    setIsOpen(false);
                }, role: "button", tabIndex: 0, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onResetView();
                        setIsOpen(false);
                    }
                } },
                react_1.default.createElement("span", null, t('main:map.controls.resetView'))),
            react_1.default.createElement("div", { className: "tr__map-controls-dropdown-group-title" },
                t('main:map.controls.background'),
                ":"),
            (0, projectBaseMapLayers_1.getProjectMapBaseLayers)().map((layer) => {
                const zoomHint = (0, projectBaseMapLayers_1.getBasemapZoomHintMessage)(layer.shortname, currentZoom, t);
                return (react_1.default.createElement(LayerOption, { key: layer.shortname, layerType: layer.shortname, label: (0, projectBaseMapLayers_1.formatBasemapDisplayName)(layer, t), isActive: currentLayer === layer.shortname, disabled: zoomHint !== null, onSelect: handleSelect, suffix: zoomHint ? (react_1.default.createElement("span", { style: { fontStyle: 'oblique', fontWeight: 'normal', marginLeft: '0.5rem' } }, zoomHint)) : undefined }));
            }),
            react_1.default.createElement("div", { className: "tr__map-controls-dropdown-group-title", style: { marginTop: '0.5rem' } },
                t('main:map.controls.overlayOpacity'),
                ":"),
            react_1.default.createElement("div", { className: "tr__map-controls-slider-container" },
                react_1.default.createElement("input", { className: "tr__map-controls-slider-input", type: "range", min: 0, max: 100, step: 1, value: localOpacity, onChange: (e) => {
                        const val = Number(e.target.value);
                        setLocalOpacity(val);
                        onOverlayOpacityChange(val);
                    }, "aria-label": t('main:map.controls.overlayOpacity') }),
                react_1.default.createElement("span", { className: "tr__map-controls-slider-value" },
                    localOpacity,
                    "%")),
            react_1.default.createElement("div", { className: "tr__map-controls-dropdown-group-title" },
                t('main:map.controls.overlayColor'),
                ":"),
            react_1.default.createElement("div", { className: "tr__map-controls-color-container" },
                react_1.default.createElement("div", { className: "tr__map-controls-radio-group" },
                    react_1.default.createElement("label", { className: "tr__map-controls-radio-item" },
                        react_1.default.createElement("input", { type: "radio", name: "overlayColor", value: "black", checked: localColor === 'black', onChange: () => {
                                setLocalColor('black');
                                onOverlayColorChange('black');
                            } }),
                        t('main:Black')),
                    react_1.default.createElement("label", { className: "tr__map-controls-radio-item" },
                        react_1.default.createElement("input", { type: "radio", name: "overlayColor", value: "white", checked: localColor === 'white', onChange: () => {
                                setLocalColor('white');
                                onOverlayColorChange('white');
                            } }),
                        t('main:White')))))));
};
/**
 * Map configuration control (basemap, overlay, reset view), registered as one MapLibre IControl.
 * Other controls, such as the polygon tool, stay separate and stack in the same corner.
 * Must be rendered as a child of `MapLibreMap`.
 * @see https://maplibre.org/maplibre-gl-js/docs/API/type-aliases/ControlPosition/
 */
const MapConfigurationPanel = (props) => {
    const [isOnMap, setIsOnMap] = react_1.default.useState(false);
    // Portal only after useControl's onAdd: map.addControl has already inserted the node.
    // Portaling from IControl.onAdd targets a detached node and React renders the button in-flow.
    const control = (0, maplibre_1.useControl)(() => new MapControlsControl(), () => setIsOnMap(true), () => setIsOnMap(false), { position: 'top-right' });
    if (!isOnMap)
        return null;
    return (0, react_dom_1.createPortal)(react_1.default.createElement(MapControlsMenu, Object.assign({}, props)), control.getContainer());
};
exports.default = MapConfigurationPanel;
//# sourceMappingURL=TransitionMapControlsMenu.js.map